import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'store/hooks';
import { CatalogWorkunit } from 'types/entities/CatalogWorkunit';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import { OrderStatus } from 'types/enums/OrderStatus';
import Scope from 'types/entities/Scope';
import PurchaseOrders from 'types/entities/PurchaseOrders';
import { SelectedWorkunit } from 'types/entities/SelectedWorkunit';
import useOrderWorkunitApi from '../../../services/useOrderWorkunitApi';
import './PurchaseOrderCell.scss';

type PurchaseOrderCellProps = {
  row: OrderWorkunit | CatalogWorkunit;
  disabled?: boolean;
  cellType: 'catalog' | 'selected';
};

const PurchaseOrderCell = (props: PurchaseOrderCellProps) => {
  const { row, disabled = false, cellType } = props;
  const poList = useSelector((state) => state.purchaseOrder?.datas);
  const isProductionOrder = useSelector((state) => state.catalogToOrder.order?.status === OrderStatus.PRODUCTION);
  const isDisabled = disabled || isProductionOrder;
  const { changeWorkunitsPo } = useOrderWorkunitApi();

  const getRowId = (): number => {
    const isSelected = (r: any): r is SelectedWorkunit => 'randomId' in r;
    return isSelected(row) ? row.randomId ?? row.id : row.id;
  };

  useEffect(() => {
    if (row.po_id === null && row.po_name === null && poList && poList.length > 0) {
      if (poList[0].is_valid === true) {
        const firstPo = poList[0];
        row.po_name = firstPo;
        row.po_id = firstPo.id;
        changeWorkunitsPo(firstPo, getRowId(), cellType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, poList]);

  const handleChange = (event: SelectChangeEvent<number | string>) => {
    const { value } = event.target;
    const rowId = getRowId();

    if (value === '') {
      changeWorkunitsPo(null, rowId, cellType, true);
      return;
    }

    const po = poList?.find((p) => p.id === Number(value));
    if (po) changeWorkunitsPo(po, rowId, cellType);
  };

  const validPoList: PurchaseOrders[] = (poList || []).filter((po) => po.name !== 'not_attribued');

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <Select
            size="small"
            fullWidth
        displayEmpty
        disabled={isDisabled}
        value={row.po_id ?? ''}
        onChange={handleChange}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ fontSize: '.9rem' }}
      >
        <MenuItem value="">
          <span style={{ color: 'rgba(0,0,0,0.42)' }}>Purchase order</span>
        </MenuItem>
        {validPoList.map((po) => (
          <MenuItem key={po.id} value={po.id}>
            {po.reference}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default (props: PurchaseOrderCellProps) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => <PurchaseOrderCell {...props} />, [props.row?.po_name]);
